import React, { useState } from 'react';
import { Modal, Form, Input, Radio, DatePicker, Button, Space, Tag, message as antdMessage } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import moment from 'moment'; // 确保安装了 moment 或使用 dayjs
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import locale from 'antd/es/date-picker/locale/zh_CN';
import './NutritionModal.less';

const { CheckableTag } = Tag;
dayjs.locale('zh-cn');

// 自定义多选标签组组件
const TagGroup = ({ options, value = [], onChange, multiple = true }) => {
  const toggleTag = (tag, checked) => {
    const nextSelectedTags = checked
      ? multiple ? [...value, tag] : [tag]
      : value.filter((t) => t !== tag);
    onChange(nextSelectedTags);
  };

  return (
    <div className="custom-tag-group">
      {options.map((option) => (
        <CheckableTag
          key={option}
          checked={value.includes(option)}
          onChange={(checked) => toggleTag(option, checked)}
        >
          {option}
        </CheckableTag>
      ))}
    </div>
  );
};

const NutritionModal = ({ open, onCancel, onSubmit }) => {
  const [form] = Form.useForm();
  // 监听健康需求的变化，用于控制疾病项的显示
  const healthNeeds = Form.useWatch('health_need', form);

// 计算精确年龄并校验限制
  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const birth = dayjs(values.birthday);
      const now = dayjs();

      // 1. 计算总月份差
      const totalMonths = now.diff(birth, 'month');

      // 2. 年龄上限：15岁9个月 = 189个月
      const MAX_MONTHS_LIMIT = 15 * 12 + 9;

      if (totalMonths > MAX_MONTHS_LIMIT) {
        // 【核心修改】：年龄超限拦截并提示
        antdMessage.error("填写生日年龄不能大于15岁9个月，请重新选择生日");
        return;
      }
      
      // 如果日期在未来（月份差为负）
      if (totalMonths < 0) {
        antdMessage.warning("生日不能晚于今天哦");
        return;
      }

      // 计算展示用的岁和月
      const years = Math.floor(totalMonths / 12);
      const months = totalMonths % 12;
      
      const finalData = {
        gender: values.gender,
        age: String(years),
        month: String(months),
        height: String(values.height),
        weight: String(values.weight),
        health_need: (values.health_need || []).join('、'),
        tell_disease: (values.tell_disease || []).join('、')
      };
      
      onSubmit(finalData);
      form.resetFields();
    } catch (error) {
      console.error('校验失败:', error);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      closable={false}
      centered
      width={400}
      className="nutrition-custom-modal"
      maskClosable={false}
    >
      <div className="modal-header">
        <CloseOutlined className="close-icon" onClick={onCancel} />
      </div>

      <Form form={form} layout="horizontal" labelCol={{ span: 7 }} labelAlign="left">
        <Form.Item name="gender" label="孩子性别" rules={[{ required: true, message: '请选择性别' }]}>
          <Radio.Group>
            <Radio value="男">男</Radio>
            <Radio value="女">女</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item name="birthday" label="生日" rules={[{ required: true, message: '请选择生日' }]}>
          <DatePicker inputReadOnly={true} locale={locale}  format="YYYY-MM-DD" placeholder="请输入" style={{ width: '100%' }} variant="outlined" disabledDate={(current) => current && current > dayjs().endOf("day")} />
        </Form.Item>

        <Form.Item name="height" label="目前身高" rules={[{ required: true, message: '请输入身高' }]}>
          <Input placeholder="请输入" suffix="cm" variant="outlined" />
        </Form.Item>

        <Form.Item name="weight" label="目前体重" rules={[{ required: true, message: '请输入体重' }]}>
          <Input placeholder="请输入" suffix="kg" variant="outlined" />
        </Form.Item>

        <Form.Item name="health_need" label="健康需求" rules={[{ required: true, message: '请选择健康需求' }]}>
          <TagGroup 
            options={['无要求', '长高', '护眼', '神经/成长发育障碍特殊需求']} 
          />
        </Form.Item>

        {/* 条件渲染：只有选择了特定需求才显示疾病选项 */}
        {healthNeeds?.includes('神经/成长发育障碍特殊需求') && (
          <Form.Item name="tell_disease" label="患有">
            <TagGroup 
              options={['孤独症 (ASD)', '抽动障碍 (TD)', '注意缺陷多动障碍 (ADHD)', '身高发育障碍', '牛奶蛋白过敏']} 
            />
          </Form.Item>
        )}

        <div className="form-footer">
          <Button type="primary" onClick={handleOk} className="submit-btn">
            提交信息
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default NutritionModal;